/**
 * Stores corrections: seedtrac STORES' admin-only Transactions menu (Good to Damage, Damage to
 * Good, Excess/Shortage, Inter Item Transfer, Opening Stock).
 *
 * Single step, like legacy (user, 2026-10-07): whoever holds `stores.correct` raises one, previews it
 * and Final Submits it; no second person. Draft → Preview → Final Submit (number minted, stock
 * posted); cancelling a posted one reverses it and needs the e-mailed code.
 *
 *  - Good to Damage / Damage to Good: one item's stock out of its bins in one condition and into
 *    bins in the other (by default the same bins). The total does not change, only its condition.
 *    An optional Party (the supplier at fault, as on the legacy G2DN) and a reason.
 *  - Excess/Shortage: rows of item × bin × condition, each adding stock found (excess) or writing
 *    off stock missing (shortage), with a reason.
 *  - Inter Item Transfer: stock booked under one item moved to another item of the same unit, in
 *    the same condition (a wrong item at arrival, a renamed sticker).
 *  - Opening Stock: a sheet of what sits in the bins before NexSeed, previewed row by row and posted
 *    in one go (after the seed Opening Balance); the same file cannot be posted twice.
 */
import { toDecimal, type DecimalInput } from "./decimal";
import type { StoresCondition } from "./stores";

export const STORES_CORRECTION_KINDS = [
  "good_to_damage",
  "damage_to_good",
  "excess_shortage",
  "inter_item",
] as const;
export type StoresCorrectionKind = (typeof STORES_CORRECTION_KINDS)[number];

export const isStoresCorrectionKind = (k: string): k is StoresCorrectionKind =>
  (STORES_CORRECTION_KINDS as readonly string[]).includes(k);

export interface StoresCorrectionConfig {
  kind: StoresCorrectionKind;
  label: string;
  title: string;
  prefix: string;
  printCode: string;
  printTitle: string;
  /** Good to Damage / Damage to Good: the condition the stock leaves and the one it lands in. */
  from: StoresCondition | null;
  to: StoresCondition | null;
  hint: string;
}

export const STORES_CORRECTIONS: Record<
  StoresCorrectionKind,
  StoresCorrectionConfig
> = {
  good_to_damage: {
    kind: "good_to_damage",
    label: "Good to Damage",
    title: "Good to Damage",
    prefix: "SGD",
    printCode: "G2DN",
    printTitle: "Good to Damage Note (G2DN)",
    from: "good",
    to: "damage",
    hint: "Good stock found damaged: moved to Damage, bin by bin.",
  },
  damage_to_good: {
    kind: "damage_to_good",
    label: "Damage to Good",
    title: "Damage to Good",
    prefix: "SDG",
    printCode: "D2GN",
    printTitle: "Damage to Good Note (D2GN)",
    from: "damage",
    to: "good",
    hint: "Damage stock found fit for use: moved back to Good.",
  },
  excess_shortage: {
    kind: "excess_shortage",
    label: "Excess / Shortage",
    title: "Excess / Shortage",
    prefix: "SES",
    printCode: "ESN",
    printTitle: "Excess / Shortage Note (ESN)",
    from: null,
    to: null,
    hint: "Book stock corrected to the floor: stock found added, stock missing written off.",
  },
  inter_item: {
    kind: "inter_item",
    label: "Inter Item Transfer",
    title: "Inter Item Transfer",
    prefix: "SIT",
    printCode: "IITN",
    printTitle: "Inter Item Transfer Note (IITN)",
    from: null,
    to: null,
    hint: "Stock booked under one item moved to another item of the same unit.",
  },
};

export const storesCorrection = (k: StoresCorrectionKind) =>
  STORES_CORRECTIONS[k];

export const STORES_OPENING_PREFIX = "SOS";
export const STORES_CORRECTION_PREFIXES = [
  ...Object.values(STORES_CORRECTIONS).map((c) => c.prefix),
  STORES_OPENING_PREFIX,
];

/** A bin and how much of an item comes out of it or goes into it. */
export interface StoresBinQty {
  locationId: number | null | undefined;
  ups: number;
  qty: DecimalInput;
}

export function binTotals(rows: readonly StoresBinQty[]): {
  ups: number;
  qty: string;
} {
  let ups = 0;
  let qty = toDecimal(0);
  for (const r of rows) {
    ups += r.ups || 0;
    qty = qty.plus(toDecimal(r.qty || 0));
  }
  return { ups, qty: qty.toFixed(3) };
}

const isZero = (r: StoresBinQty) => !r.ups && toDecimal(r.qty || 0).isZero();

/** Good to Damage, Damage to Good and Inter Item Transfer: what Preview still wants. */
export interface StoresMoveCorrectionInput {
  itemId: number | null | undefined;
  /** Inter Item Transfer only: the item the stock is moved to, and the condition it sits in. */
  toItemId?: number | null | undefined;
  condition?: StoresCondition | null | undefined;
  picks: readonly StoresBinQty[];
  puts: readonly StoresBinQty[];
  remarks?: string | null | undefined;
}

export function storesMoveCorrectionBlockers(
  kind: Exclude<StoresCorrectionKind, "excess_shortage">,
  d: StoresMoveCorrectionInput
): string[] {
  const out: string[] = [];
  if (!d.itemId) out.push(kind === "inter_item" ? "From Item" : "Stores Item");
  if (kind === "inter_item") {
    if (!d.condition) out.push("Select Type");
    if (!d.toItemId) out.push("To Item");
    else if (d.toItemId === d.itemId)
      out.push("a To Item other than the From Item");
  }
  const picks = d.picks.filter((p) => !isZero(p));
  const puts = d.puts.filter((p) => !isZero(p));
  if (!picks.length) out.push("at least one bin to take stock from");
  if (!puts.length) out.push("at least one bin to put it into");
  if (puts.some((p) => !p.locationId)) out.push("a bin on every put row");
  if (picks.length && puts.length) {
    const a = binTotals(picks);
    const b = binTotals(puts);
    if (a.ups !== b.ups || a.qty !== b.qty)
      out.push(
        `put rows adding up to what is taken (${a.ups} UPS / ${a.qty}; now ${b.ups} / ${b.qty})`
      );
  }
  if (!d.remarks?.trim()) out.push("Reason");
  return out;
}

export const STORES_ADJUST_DIRECTIONS = [
  { value: "excess", label: "Excess (found)" },
  { value: "shortage", label: "Shortage (missing)" },
] as const;
export type StoresAdjustDirection =
  (typeof STORES_ADJUST_DIRECTIONS)[number]["value"];

export interface StoresAdjustRowInput {
  itemId: number | null | undefined;
  locationId: number | null | undefined;
  condition: StoresCondition | null | undefined;
  direction: StoresAdjustDirection | null | undefined;
  ups: number;
  qty: DecimalInput;
}

/** Excess/Shortage: what Preview still wants. */
export function storesAdjustBlockers(d: {
  rows: readonly StoresAdjustRowInput[];
  remarks?: string | null | undefined;
}): string[] {
  const out: string[] = [];
  if (!d.rows.length) out.push("at least one row");
  d.rows.forEach((r, i) => {
    const miss = [
      !r.itemId && "item",
      !r.locationId && "bin",
      !r.condition && "Good/Damage",
      !r.direction && "Excess or Shortage",
      isZero({ locationId: r.locationId, ups: r.ups, qty: r.qty }) &&
        "UPS or Qty",
    ].filter(Boolean);
    if (miss.length) out.push(`row ${i + 1}: ${miss.join(", ")}`);
  });
  const keys = d.rows
    .filter((r) => r.itemId && r.locationId && r.condition)
    .map((r) => `${r.itemId}:${r.locationId}:${r.condition}`);
  if (new Set(keys).size !== keys.length)
    out.push("each item, bin and Good/Damage once only");
  if (!d.remarks?.trim()) out.push("Reason");
  return out;
}

/** Opening Stock sheet: the condition column, read loosely (G, Good, D, Damage, Damaged). */
export function openingCondition(v: string): StoresCondition | null {
  const k = v.trim().toLowerCase();
  if (k === "g" || k === "good") return "good";
  if (k === "d" || k === "damage" || k === "damaged") return "damage";
  return null;
}
